import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import { X, Plus, Minus, Loader2, RotateCcw } from 'lucide-react'
import { RootState } from '../../app/store'
import { useGetCashierMenuQuery } from '../../features/menu/menuAPI'
import { selectSelectedTypeId, selectIsCsFlowComplete } from '../../features/csFlow/csFlowSlice'
import { isCustomerServiceUser } from '../../utils/apiUtils'
import { addOrderItems, removeOrderItem } from '../../services/ordersService'
import SearchBarSection from '../home/SearchBarSection'
import CategoryTabs from '../home/CategoryTabs'
import ProductsGridSection from '../home/ProductsGridSection'
import ProductDetailsModal from '../product/ProductDetailsModal'
import CurrencyDisplay from '../shared/CurrencyDisplay'
import deletes from '../../assets/images/delete.svg'
import dalaSvg from '../../assets/images/salle.svg'

export interface EditableOrderItem {
  id: number
  item_id?: number
  product_id?: string | number
  quantity: number
  price?: number
  total_price?: number
  meals: Array<{
    id: number
    name: string
    price?: number
    image?: string
    additionals: Array<{
      id: number
      name: string
      price?: number
    }>
  }>
}

interface EditRow {
  key: string
  kind: 'existing' | 'new'
  orderItemId?: number
  originalQuantity: number
  itemId: number
  mealId: number
  hasMeals: boolean
  additionals: Array<{ id: number; name?: string }>
  name: string
  unitPrice: number
  quantity: number
  image?: string
}

interface EditOrderModalProps {
  isOpen: boolean
  orderId: string | number
  order: {
    id: number
    statusId: number
    typeId: number
    branch?: string
    items: EditableOrderItem[]
  }
  isRTL: boolean
  onClose: () => void
  onSaved: () => void | Promise<void>
}

interface Mutation {
  id: string
  run: () => Promise<void>
}

const EditOrderModal: React.FC<EditOrderModalProps> = ({
  isOpen,
  orderId,
  order,
  isRTL,
  onClose,
  onSaved,
}) => {
  const { t } = useTranslation()

  // Menu data source: same as HomePage but driven by the existing order's type
  const { user } = useSelector((state: RootState) => state.auth)
  const csBranchId = useSelector((state: RootState) => state.csFlow.selectedBranchId)
  const csTypeId = useSelector(selectSelectedTypeId)
  const isCsFlowComplete = useSelector(selectIsCsFlowComplete)
  const isCustomerService = isCustomerServiceUser()

  const settings = useSelector((state: RootState) => state.settings.settings)

  const branchId = useMemo(() => {
    if (isCustomerService && csBranchId) return csBranchId
    if (user?.branch?.id) {
      const id = typeof user.branch.id === 'number' ? user.branch.id : Number(user.branch.id)
      if (!isNaN(id) && id > 0) return id
    }
    try {
      const employeeDataStr = localStorage.getItem('employee_data')
      if (employeeDataStr) {
        const employeeData = JSON.parse(employeeDataStr)
        if (employeeData.branch?.id) {
          const id = typeof employeeData.branch.id === 'number'
            ? employeeData.branch.id
            : Number(employeeData.branch.id)
          if (!isNaN(id) && id > 0) return id
        }
      }
    } catch (e) {
      return null
    }
    return null
  }, [isCustomerService, csBranchId, user])

  const activeTypeId = useMemo(() => {
    const orderTypeId = Number(order?.typeId)
    if (orderTypeId > 0) return orderTypeId
    if (isCustomerService && csTypeId) return csTypeId
    return 1
  }, [order?.typeId, isCustomerService, csTypeId])

  // Tab state
  const [activeTab, setActiveTab] = useState<'current' | 'add'>('current')

  // Local edit session state
  const [rows, setRows] = useState<EditRow[]>([])
  const newRowCounter = useRef(0)
  const completedMutations = useRef<Set<string>>(new Set())

  // Add Item tab local state (search / category / product modal)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  // Product Details is a STANDALONE foreground modal, never nested over Edit
  // Order. While it is open the Edit Order shell is hidden (display:none) but
  // kept mounted so tab / category / search / scroll / item edits are preserved.
  const [selectedProductForDetails, setSelectedProductForDetails] = useState<any>(null)
  const productsScrollRef = useRef<HTMLDivElement>(null)
  const savedProductsScroll = useRef(0)

  // Save state
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  const menuArgs = useMemo(
    () => ({ branchId: branchId as string | number, typeId: activeTypeId }),
    [branchId, activeTypeId]
  )

  const {
    data: menuData,
    isLoading: menuLoading,
  } = useGetCashierMenuQuery(
    { branchId: menuArgs.branchId, typeId: menuArgs.typeId, isCustomerService },
    { skip: !isOpen || !menuArgs.branchId || activeTab !== 'add' || (isCustomerService && !isCsFlowComplete) }
  )

  // Snapshot the current order items when the modal opens
  useEffect(() => {
    if (!isOpen) return

    const existingRows: EditRow[] = (order?.items || []).map((item) => {
      const quantity = Number(item.quantity) || 1
      const meal = item.meals?.[0]
      const unitPrice = Number(item.price) > 0
        ? Number(item.price)
        : (Number(item.total_price) || 0) / quantity
      return {
        key: `existing-${item.id}`,
        kind: 'existing' as const,
        orderItemId: Number(item.id),
        originalQuantity: quantity,
        itemId: Number(item.item_id || meal?.id || item.product_id || item.id),
        mealId: Number(meal?.id || item.product_id || item.id),
        hasMeals: (item.meals?.length || 0) > 0,
        additionals: (meal?.additionals || []).map((a) => ({ id: Number(a.id), name: String(a.name || '') })),
        name: meal?.name || '',
        unitPrice: isNaN(unitPrice) ? 0 : unitPrice,
        quantity,
        image: meal?.image || '',
      }
    })

    setRows(existingRows)
    setActiveTab('current')
    setSearchQuery('')
    setSelectedCategory(null)
    setSelectedProductForDetails(null)
    setSaveError(null)
    setIsSaving(false)
    newRowCounter.current = 0
    completedMutations.current = new Set()
    savedProductsScroll.current = 0
  }, [isOpen, order])

  // Restore the Add Item product list scroll when returning from Product Details
  useEffect(() => {
    if (!selectedProductForDetails && productsScrollRef.current) {
      productsScrollRef.current.scrollTop = savedProductsScroll.current
    }
  }, [selectedProductForDetails])

  // Flatten menu data (mirrors HomePage)
  const allProducts = useMemo(() => {
    if (!menuData) return []

    const source = Array.isArray(menuData)
      ? menuData
      : (menuData && typeof menuData === 'object' && 'categories' in menuData && Array.isArray((menuData as any).categories))
        ? (menuData as any).categories
        : []

    const products: any[] = []
    source.forEach((category: any) => {
      if (category.products && Array.isArray(category.products)) {
        category.products.forEach((product: any) => {
          if (product.id && product.name && product.image && product.price) {
            products.push({
              ...product,
              category: category.name,
              categoryName: category.name,
            })
          }
        })
      }
    })
    return products
  }, [menuData])

  const menuCategories = useMemo(() => {
    if (!menuData) return []
    const source = Array.isArray(menuData)
      ? menuData
      : (menuData && typeof menuData === 'object' && 'categories' in menuData && Array.isArray((menuData as any).categories))
        ? (menuData as any).categories
        : []
    return source.map((category: any) => category.name || category.name_en).filter(Boolean)
  }, [menuData])

  // Default to first category when menu loads (mirrors HomePage)
  useEffect(() => {
    if (!isOpen || activeTab !== 'add') return
    if (!selectedCategory && menuCategories.length > 0) {
      setSelectedCategory(menuCategories[0])
    }
  }, [menuCategories, selectedCategory, isOpen, activeTab])

  const filteredProducts = useMemo(() => {
    const query = (searchQuery || '').trim().toLowerCase()
    if (!query) {
      if (!selectedCategory) return allProducts
      return allProducts.filter((p: any) =>
        p.category === selectedCategory || p.categoryName === selectedCategory)
    }
    return allProducts.filter((p: any) => {
      const name = String(p.name || '').toLowerCase()
      const nameEn = String(p.name_en || '').toLowerCase()
      const description = String(p.description || '').toLowerCase()
      return name.includes(query) || nameEn.includes(query) || description.includes(query)
    })
  }, [allProducts, searchQuery, selectedCategory])

  // Count added / removed rows to reflect local changes on the tab
  const addedRowsCount = useMemo(
    () => rows.filter((r) => r.kind === 'new').length,
    [rows]
  )

  // Reset the row IDs that have been successfully applied to the backend
  const applyMutation = useCallback(async (m: Mutation) => {
    if (completedMutations.current.has(m.id)) return
    await m.run()
    completedMutations.current.add(m.id)
  }, [])

  const handleAddToOrder = useCallback(async (
    item: {
      itemId: number
      quantity: number
      meals: Array<{ mealId: number; additionals: Array<{ id: number }> }>
    },
    productInfo: { id: string | number; name?: string; price?: number; image?: string }
  ) => {
    newRowCounter.current += 1
    const key = `new-${Date.now()}-${newRowCounter.current}`
    setRows((prev) => [
      ...prev,
      {
        key,
        kind: 'new',
        originalQuantity: item.quantity,
        itemId: item.itemId,
        mealId: item.meals?.[0]?.mealId ?? item.itemId,
        hasMeals: true,
        additionals: item.meals?.[0]?.additionals ?? [],
        name: productInfo.name || '',
        unitPrice: Number(productInfo.price) || 0,
        quantity: item.quantity,
        image: productInfo.image || '',
      },
    ])
  }, [])

  const changeQuantity = useCallback((key: string, delta: number) => {
    setRows((prev) =>
      prev.map((row) => {
        if (row.key !== key) return row
        const next = Math.max(0, row.quantity + delta)
        if (next === row.quantity) return row
        return { ...row, quantity: next }
      })
    )
  }, [])

  // Existing rows are zeroed (not dropped) so the save still sends a delete for them
  const removeRow = useCallback((key: string) => {
    setRows((prev) =>
      prev.flatMap((row) => {
        if (row.key !== key) return [row]
        return row.kind === 'existing' ? [{ ...row, quantity: 0 }] : []
      })
    )
  }, [])

  // Build the list of API mutations for the current edit session
  const buildMutations = useCallback((): Mutation[] => {
    const mutations: Mutation[] = []
    // Deletes run after adds: the backend rejects removing the order's last item
    const deletions: Mutation[] = []

    for (const row of rows) {
      if (row.kind === 'existing' && row.orderItemId != null) {
        const removed = row.originalQuantity - row.quantity
        if (removed > 0) {
          deletions.push({
            id: `delete-${row.orderItemId}-${removed}`,
            run: () => removeOrderItem(orderId, row.itemId, removed),
          })
        } else if (row.quantity > row.originalQuantity) {
          const added = row.quantity - row.originalQuantity
          mutations.push({
            id: `add-existing-${row.orderItemId}-${added}`,
            run: () =>
              addOrderItems(orderId, [
                {
                  itemId: row.itemId,
                  quantity: added,
                  meals: row.hasMeals
                    ? [{ mealId: row.mealId, additionals: row.additionals.map((a) => ({ id: a.id })) }]
                    : [],
                },
              ]),
          })
        }
      } else if (row.kind === 'new') {
        mutations.push({
          id: `add-new-${row.key}`,
          run: () =>
            addOrderItems(orderId, [
              {
                itemId: row.itemId,
                quantity: row.quantity,
                meals: row.hasMeals
                  ? [{ mealId: row.mealId, additionals: row.additionals.map((a) => ({ id: a.id })) }]
                  : [],
              },
            ]),
        })
      }
    }

    return [...mutations, ...deletions]
  }, [rows, orderId])

  const getErrorMessage = (err: any): string =>
    err?.response?.data?.descriptiveMessage ||
    err?.response?.data?.message ||
    err?.message ||
    t('orders.edit_order_error') ||
    'حدث خطأ أثناء تعديل الطلب'

  const handleSave = async () => {
    if (isSaving) return

    const mutations = buildMutations()
    if (mutations.length === 0) {
      onClose()
      return
    }

    setIsSaving(true)
    setSaveError(null)
    try {
      for (const m of mutations) {
        try {
          await applyMutation(m)
        } catch (err: any) {
          const msg = getErrorMessage(err)
          toast.error(msg)
          setSaveError(msg)
          setIsSaving(false)
          return
        }
      }

      toast.success(t('orders.edit_order_success') || 'تم تعديل الطلب بنجاح')
      if (onSaved) {
        await onSaved()
      }
      setIsSaving(false)
      onClose()
    } catch (err: any) {
      const msg = getErrorMessage(err)
      toast.error(msg)
      setSaveError(msg)
      setIsSaving(false)
    }
  }

  const rowTotal = (row: EditRow): number => row.unitPrice * row.quantity

  if (!isOpen) return null

  return (
    <>
      <div
        className="fixed inset-0 z-40 flex items-center justify-center py-6"
        style={selectedProductForDetails ? { display: 'none' } : undefined}
        dir={isRTL ? 'rtl' : 'ltr'}
        onClick={(e) => {
          if (e.target === e.currentTarget && !isSaving) onClose()
        }}
      >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/30" />

      {/* Modal Container */}
      <div className="relative w-[760px] max-w-[94vw] h-[720px] max-h-[92vh] rounded-2xl bg-[#EBEBEB] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex-none px-4 pt-4 pb-3 flex items-center gap-3">
          <span className="flex-1 text-text-primary text-lg font-semibold font-['IBM Plex Sans Arabic']">
            {t('orders.edit_order') || 'تعديل الطلب'}
          </span>
          <button
            onClick={onClose}
            disabled={isSaving}
            className="w-10 h-10 bg-white rounded-xl border border-gray-200 flex items-center justify-center disabled:opacity-50"
          >
            <X className="w-6 h-6 text-icon-secondary" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex-none px-4 border-b border-border-soft">
          <div className="flex items-center gap-6">
            <button
              onClick={() => setActiveTab('current')}
              className={`h-12 flex items-center gap-2 border-b-2 transition-colors ${
                activeTab === 'current'
                  ? 'border-flutter-button text-flutter-button font-semibold'
                  : 'border-transparent text-text-secondary font-medium'
              }`}
            >
              <span className="text-sm font-['IBM Plex Sans Arabic']">
                {t('orders.current_items') || 'الأصناف'}
              </span>
              {addedRowsCount > 0 && (
                <span className="px-2 h-5 min-w-5 rounded-full bg-flutter-button text-white text-xs font-semibold flex items-center justify-center">
                  {addedRowsCount}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('add')}
              className={`h-12 border-b-2 transition-colors ${
                activeTab === 'add'
                  ? 'border-flutter-button text-flutter-button font-semibold'
                  : 'border-transparent text-text-secondary font-medium'
              }`}
            >
              <span className="text-sm font-['IBM Plex Sans Arabic']">
                {t('orders.add_item') || 'اضافة صنف'}
              </span>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-h-0 flex flex-col">
          {activeTab === 'current' ? (
            /* ---- Current Items ---- */
            <div className="flex-1 min-h-0 overflow-y-auto hide-scrollbar">
              {rows.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full">
                  <img src={dalaSvg} className="w-14 h-14 mb-2" />
                  <p className="text-base text-gray-700 font-['IBM Plex Sans Arabic']">
                    {t('orders.no_items_in_order') || 'لا توجد أصناف في الطلب'}
                  </p>
                </div>
              ) : (
                <div className="flex flex-col gap-3 p-4">
                  {rows.map((row) => {
                    const isRemoved = row.quantity === 0
                    return (
                      <div
                        key={row.key}
                        className={`bg-white rounded-2xl border border-border-soft p-3 flex flex-col gap-3 ${isRemoved ? 'opacity-60' : ''}`}
                      >
                        <div className="flex items-center gap-3">
                          {/* Image */}
                          <div className="w-14 h-14 rounded-xl overflow-hidden flex-shrink-0 bg-neutral-100">
                            <img
                              src={row.image || 'https://placehold.co/56x56'}
                              alt={row.name}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.currentTarget.src = 'https://placehold.co/56x56'
                              }}
                            />
                          </div>

                          {/* Name + price */}
                          <div className="flex-1 min-w-0 flex flex-col items-start gap-0.5">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="text-sm font-medium font-['IBM Plex Sans Arabic'] text-text-primary truncate">
                                {row.name}
                              </span>
                              {row.kind === 'new' && (
                                <span className="px-2 py-0.5 rounded-full bg-green-50 border border-green-200 text-green-600 text-[11px] font-semibold whitespace-nowrap">
                                  {t('orders.new_item') || 'جديد'}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="text-sm font-semibold text-text-primary">
                                {rowTotal(row).toFixed(2)}
                              </span>
                              <CurrencyDisplay className="w-4 h-4" />
                            </div>
                            {isRemoved && (
                              <span className="text-xs font-['IBM Plex Sans Arabic'] text-red-500">
                                {t('orders.removed_item') || 'سيتم حذف هذا الصنف'}
                              </span>
                            )}
                          </div>

                          {/* Quantity + delete */}
                          <div className="flex items-center gap-2">
                            <div className="h-10 rounded-xl border border-border-soft flex items-center bg-white overflow-hidden">
                              <button
                                onClick={() => changeQuantity(row.key, -1)}
                                disabled={isSaving || row.quantity <= 0}
                                className="w-10 h-full flex items-center justify-center hover:bg-gray-50 disabled:opacity-40"
                              >
                                <Minus className="w-5 h-5 text-[#525866]" />
                              </button>
                              <div className="w-10 h-full flex items-center justify-center border-x border-border-soft">
                                <span className="text-sm font-semibold text-text-primary">
                                  {row.quantity}
                                </span>
                              </div>
                              <button
                                onClick={() => changeQuantity(row.key, 1)}
                                disabled={isSaving}
                                className="w-10 h-full flex items-center justify-center hover:bg-gray-50 disabled:opacity-40"
                              >
                                <Plus className="w-5 h-5 text-[#525866]" />
                              </button>
                            </div>

                            {row.quantity === 0 ? (
                              <button
                                onClick={() => changeQuantity(row.key, 1)}
                                disabled={isSaving}
                                className="h-10 w-10 rounded-xl border border-gray-200 flex items-center justify-center hover:bg-gray-50 disabled:opacity-50"
                                title={t('orders.restore_item') || 'استعادة الصنف'}
                              >
                                <RotateCcw className="w-5 h-5 text-icon-primary" />
                              </button>
                            ) : (
                              <button
                                onClick={() => removeRow(row.key)}
                                disabled={isSaving}
                                className="h-10 w-10 rounded-xl border border-gray-200 flex items-center justify-center hover:bg-gray-50 disabled:opacity-50"
                              >
                                <img src={deletes} className="w-5 h-5" alt={t('common.delete') || 'حذف'} />
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Additionals pills */}
                        {row.additionals.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {row.additionals.map((a) => (
                              <div
                                key={a.id}
                                className="px-2.5 py-0.5 rounded-full border border-gray-200 bg-white text-[#525866]"
                              >
                                <span className="text-xs leading-4 font-['IBM Plex Sans Arabic']">
                                  {a.name || a.id}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          ) : (
            /* ---- Add Item ---- */
            <div className="flex-1 min-h-0 flex flex-col">
              <div className="flex-none px-4 pt-4">
                <SearchBarSection
                  searchQuery={searchQuery}
                  onSearchChange={setSearchQuery}
                  isRTL={isRTL}
                  placeholder={t('home.search_placeholder') || 'ابحث عن منتج...'}
                />
              </div>
              <div className="flex-none">
                <CategoryTabs
                  categories={menuCategories}
                  selectedCategory={selectedCategory}
                  onCategorySelect={(cat) => setSelectedCategory(cat ? String(cat) : null)}
                  isLoading={menuLoading}
                />
              </div>
              <ProductsGridSection
                products={filteredProducts}
                isLoading={menuLoading}
                currency={settings.currency}
                scrollRef={productsScrollRef}
                onProductSelect={(product) => {
                  if (productsScrollRef.current && activeTab === 'add') {
                    savedProductsScroll.current = productsScrollRef.current.scrollTop
                  }
                  setSelectedProductForDetails(product)
                }}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex-none px-4 py-3 border-t border-border-soft flex items-center gap-3 justify-end">
          {saveError ? (
            <span className="flex-1 text-sm font-['IBM Plex Sans Arabic'] text-red-500">
              {saveError}
            </span>
          ) : (
            <span className="flex-1" />
          )}

          <button
            onClick={onClose}
            disabled={isSaving}
            className="h-11 px-6 bg-white rounded-lg border border-border-soft flex items-center justify-center hover:bg-gray-50 disabled:opacity-50"
          >
            <span className="text-sm font-semibold font-['IBM Plex Sans Arabic'] text-text-primary">
              {t('common.cancel')}
            </span>
          </button>

          <button
            onClick={handleSave}
            disabled={isSaving}
            className="h-11 px-8 bg-flutter-button rounded-lg flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {isSaving && <Loader2 className="w-5 h-5 animate-spin text-white" />}
            <span className="text-sm font-semibold font-['IBM Plex Sans Arabic'] text-white">
              {isSaving
                ? (t('common.processing') || 'جاري الحفظ...')
                : (t('common.save') || 'حفظ')}
            </span>
          </button>
        </div>
      </div>
      </div>

      {/* Product Details — STANDALONE foreground modal.
          While open, the Edit Order shell above is display:none so exactly one
          modal is visible. Closing restores Edit Order exactly as it was. */}
      {selectedProductForDetails && (
        <ProductDetailsModal
          isOpen
          onClose={() => setSelectedProductForDetails(null)}
          product={selectedProductForDetails}
          onAddToOrder={handleAddToOrder}
        />
      )}
    </>
  )
}

export default EditOrderModal